import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	useLoaderData,
	useNavigation,
	useSearchParams,
	useSubmit,
} from '@remix-run/react'
import {
	AlertTriangleIcon,
	ArrowLeftIcon,
	ArrowRightIcon,
	MailCheckIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Alert, AlertDescription } from '#app/components/ui/alert'
import { Button } from '#app/components/ui/button'
import { Input } from '#app/components/ui/input'
import { authenticator } from '#app/utils/auth.server'
import { commitSession, getSession } from '#app/utils/session.server'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/login' }],
}

export const meta: MetaFunction = () => [
	{ title: `Login | Erda` },
	{
		name: 'description',
		content: `Login to Erda to access your Plots. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'keywords',
		content: 'login, sign in, sign up',
	},
	{ name: 'og:title', content: `Login | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/login.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Login to Erda to access your Plots. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/login.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Login | Erda` },
	{
		name: 'twitter:description',
		content: `Login to Erda to access your Plots. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
]

type LoaderData = {
	user?: User
	email: string
	error?: { message: string }
}

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const redirectTo = url.searchParams.get('redirectTo')

	const successRedirect = redirectTo
		? `/account?redirectTo=${redirectTo}`
		: '/account'

	const userSession = await authenticator.isAuthenticated(request, {
		successRedirect,
	})

	const session = await getSession(request.headers.get('cookie'))
	const email = session.get('auth:email') as string
	const error = session.get(authenticator.sessionErrorKey)

	return json(
		{ user: userSession, email, error },
		{
			headers: {
				'Set-Cookie': await commitSession(session),
			},
		},
	)
}

// TODO (med) on login load the user plots & favorites

export async function action({ request }: ActionFunctionArgs) {
	const url = new URL(request.url)
	const redirectTo = url.searchParams.get('redirectTo')
	const redirect = redirectTo ? `/login?redirectTo=${redirectTo}` : '/login'

	await authenticator.authenticate('TOTP', request, {
		successRedirect: redirect,
		failureRedirect: redirect,
	})
}

export default function Login() {
	const loaderData = useLoaderData() as LoaderData
	const [searchParams] = useSearchParams()

	const claimId = searchParams.get('claim')

	const navigation = useNavigation()
	const isLoading = navigation.state === 'loading'

	const submit = useSubmit()

	const destroySession = async () => {
		const formData = new FormData()
		submit(formData, {
			action: '/resources/user/destroy-session',
			method: 'post',
		})
	}

	const [resendDisabled, setResendDisabled] = useState(false)

	const resendCode = async () => {
		setResendDisabled(true)
		const formData = new FormData()
		formData.append('email', loaderData.email)
		submit(formData, {
			action: '/login',
			method: 'post',
		})
		setTimeout(() => {
			setResendDisabled(false)
		}, 10000)
	}

	return (
		<div className="m-auto flex max-w-md flex-1 flex-col justify-center  gap-6 px-6">
			{searchParams.get('redirectTo') === '/basket' && (
				<Button
					data-vaul-no-drag
					variant="link"
					className="h-auto w-fit self-center"
					asChild
				>
					<Link to="/basket">
						<ArrowLeftIcon className="h-4 w-4" /> Basket
					</Link>
				</Button>
			)}

			{loaderData?.email ? (
				<Alert className="border-none bg-emerald-100 p-2">
					<MailCheckIcon className="!top-2.5 h-4 w-4" />
					<AlertDescription className="mt-1.5 !pl-8 text-xs">
						Email sent to {loaderData.email}
					</AlertDescription>
				</Alert>
			) : (
				<span className="flex flex-col items-center gap-2 text-center text-sm">
					{claimId ? (
						<>
							Log in to claim your gift.
							<br />
							Just enter your email and we'll send you a code.
							<br />
							<span className="flex gap-1 text-xs text-muted-foreground">
								<AlertTriangleIcon className="inline-block h-4 w-4" /> Must be
								the email address the gift was sent to.
							</span>
						</>
					) : (
						<>
							No need to sign up. <br />
							Just enter your email and we'll send you a code.
						</>
					)}
				</span>
			)}

			{loaderData?.error && (
				<Alert variant="destructive" className="border-none bg-red-100 p-2">
					<AlertTriangleIcon className="!top-2.5 h-4 w-4" />
					<AlertDescription className="mt-1.5 !pl-8 text-xs">
						{loaderData.error.message}
					</AlertDescription>
				</Alert>
			)}

			{!loaderData?.user && !loaderData?.email && (
				<>
					<Form method="post" className="flex w-full flex-col gap-3">
						<div className="items-center gap-1.5">
							<Input
								data-vaul-no-drag
								type="email"
								name="email"
								placeholder="example@email.com"
								required
							/>
						</div>
						<Button type="submit" disabled={isLoading}>
							Send code
						</Button>
					</Form>
				</>
			)}

			{/* Verify Code Form. */}
			{loaderData?.email && (
				<div className="flex w-full flex-col gap-3">
					<Form
						method="post"
						autoComplete="off"
						className="flex w-full flex-col gap-3"
					>
						<div className="relative items-center gap-1.5">
							<Input
								data-vaul-no-drag
								type="text"
								name="code"
								placeholder="Paste your code here..."
								required
								className="pr-24"
							/>
							<Button
								type="submit"
								className="absolute right-2 top-1.5 flex h-7 px-2"
								disabled={isLoading}
							>
								<span>Verify</span>
								<ArrowRightIcon className="h-4 w-4" />
							</Button>
						</div>
					</Form>

					<div className="flex w-full justify-center gap-2">
						<Button
							data-vaul-no-drag
							variant="link"
							disabled={resendDisabled || isLoading}
							onClick={resendCode}
						>
							Resend code
						</Button>
						&bull;
						<Button
							data-vaul-no-drag
							variant="link"
							onClick={destroySession}
							disabled={isLoading}
						>
							Change email
						</Button>
					</div>
				</div>
			)}

			<div className="flex flex-col items-center gap-2 text-center">
				<span className="text-sm text-muted-foreground">or</span>
				<Form action={`/auth/google`} method="post">
					<Button data-vaul-no-drag>
						<svg
							className="h-6 w-6 fill-white"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M20.283 10.356h-8.327v3.451h4.792c-.446 2.193-2.313 3.453-4.792 3.453a5.27 5.27 0 0 1-5.279-5.28 5.27 5.27 0 0 1 5.279-5.279c1.259 0 2.397.447 3.29 1.178l2.6-2.599c-1.584-1.381-3.615-2.233-5.89-2.233a8.908 8.908 0 0 0-8.934 8.934 8.907 8.907 0 0 0 8.934 8.934c4.467 0 8.529-3.249 8.529-8.934 0-.528-.081-1.097-.202-1.625z" />
						</svg>
						<span>Continue with Google</span>
					</Button>
				</Form>
			</div>

			<p className="text-xs text-muted-foreground">
				By logging in, you agree to our{' '}
				<Button variant="link" asChild className="text-xs">
					<Link to="/terms?from=/login">Terms</Link>
				</Button>{' '}
				and{' '}
				<Button variant="link" asChild className="text-xs">
					<Link to="/privacy?from=/login">Privacy Policy</Link>
				</Button>
				.
			</p>

			{/* TODO (med) subscribe to newsletter */}
			{/* TODO (med) load user plots after login */}
		</div>
	)
}
